/**
 * Local-first sync (SPEC §13): Dexie is the source of truth on the device; when logged in, changes go to D1/R2
 * with last-write-wins by `updatedAt`. Processed routes and FIT files are stored in R2.
 */
import { db } from '@/db/db';
import { useAccount, isLoggedIn } from '@/store/account';
import { getSettings, useSettings } from '@/store/settings';
import { normalizeSettings, type Settings } from '@/store/settingsModel';
import type { Workout } from '@/workouts/model';
import type { PlannedRide } from '@/plan/model';
import type { ProcessedRoute } from '@/routes/model';
import type { RideRecord, RideSample } from '@/ride/model';
import { decodeFit } from '@/ride/fit';
import { api } from './api';

interface SyncResponse {
  serverTime: number;
  workouts: Workout[];
  plans: PlannedRide[];
  routes: (Omit<ProcessedRoute, 'points'> & { points?: undefined })[];
  rides: RideRecord[];
  settings: { value: Settings; updatedAt: number } | null;
}

const LAST_SYNC_KEY = 'lastSyncAt';

async function getLastSync(): Promise<number> {
  return ((await db.kv.get(LAST_SYNC_KEY))?.value as number | undefined) ?? 0;
}

let running: Promise<void> | null = null;

export function syncNow(): Promise<void> {
  if (!isLoggedIn()) return Promise.resolve();
  if (running) return running;
  running = doSync().finally(() => {
    running = null;
  });
  return running;
}

async function doSync(): Promise<void> {
  const acc = useAccount.getState();
  acc.set({ syncing: true, syncError: null });
  try {
    const since = await getLastSync();
    const changed = <T extends { updatedAt: number }>(rows: T[]) => rows.filter((r) => r.updatedAt > since);
    const workouts = changed(await db.workouts.toArray());
    const plans = changed(await db.plans.toArray());
    const routes = changed(await db.routes.toArray());
    const rides = changed(await db.rides.toArray());
    const s = getSettings();

    // Upload R2 payloads first (routes are content-addressed and shared with room members).
    for (const r of routes) {
      if (!r.deleted && !r.remoteKey) {
        const res = await api.putRoute(r.id, JSON.stringify(r));
        await db.routes.update(r.id, { remoteKey: res.key });
      }
    }
    for (const r of rides) {
      if (r.deleted || r.remoteKey) continue;
      const data = await db.rideData.get(r.id);
      if (data?.fit) {
        const res = await api.putRideFit(r.id, data.fit);
        r.remoteKey = res.key;
        await db.rides.update(r.id, { remoteKey: res.key });
      }
    }

    const resp = await api.sync<SyncResponse>({
      since,
      workouts,
      plans,
      routes: routes.map(({ points: _p, ...meta }) => meta),
      rides,
      settings: s.updatedAt > since ? { value: s, updatedAt: s.updatedAt } : null,
    });
    await applyRemote(resp);
    await db.kv.put({ key: LAST_SYNC_KEY, value: resp.serverTime });
    acc.set({ syncing: false, lastSyncAt: resp.serverTime });
  } catch (err) {
    acc.set({ syncing: false, syncError: err instanceof Error ? err.message : String(err) });
  }
}

async function applyRemote(resp: SyncResponse): Promise<void> {
  for (const w of resp.workouts) {
    const local = await db.workouts.get(w.id);
    if (!local || w.updatedAt > local.updatedAt) await db.workouts.put(w);
  }
  for (const p of resp.plans) {
    const local = await db.plans.get(p.id);
    if (!local || p.updatedAt > local.updatedAt) await db.plans.put(p);
  }
  for (const r of resp.rides) {
    const local = await db.rides.get(r.id);
    if (!local || r.updatedAt > local.updatedAt) await db.rides.put(r);
  }
  for (const r of resp.routes) {
    const local = await db.routes.get(r.id);
    if (local && local.updatedAt >= r.updatedAt) continue;
    if (r.deleted) {
      if (local) await db.routes.put({ ...local, deleted: true, updatedAt: r.updatedAt });
      continue;
    }
    try {
      const full = await api.getRoute<ProcessedRoute>(r.id);
      await db.routes.put({ ...full, updatedAt: r.updatedAt, remoteKey: `routes/${r.id}.json` });
    } catch {
      /* route payload missing — keep going */
    }
  }
  if (resp.settings && resp.settings.updatedAt > getSettings().updatedAt) {
    await useSettings.getState().replace(normalizeSettings(resp.settings.value));
  }
}

/** Samples for the summary page: local, or decoded from the FIT stored in R2 for rides from another device. */
export async function loadRideSamples(rideId: string): Promise<RideSample[]> {
  const local = await db.rideData.get(rideId);
  if (local) return local.samples;
  if (!isLoggedIn()) return [];
  try {
    const fit = await api.getRideFit(rideId);
    const samples = samplesFromFit(fit);
    await db.rideData.put({ rideId, samples, fit });
    return samples;
  } catch {
    return [];
  }
}

export function samplesFromFit(fit: Uint8Array): RideSample[] {
  const decoded = decodeFit(fit);
  const recs = decoded.messages.recordMesgs ?? [];
  const t0 = recs[0]?.timestamp instanceof Date ? (recs[0].timestamp as Date).getTime() : 0;
  return recs.map((r, i) => {
    const t = r.timestamp instanceof Date ? r.timestamp.getTime() : t0 + i * 1000;
    const dev = (r.developerFields ?? {}) as Record<number, number>;
    const s: RideSample = {
      t,
      el: i + 1,
      power: Number(r.power ?? 0),
      cadence: Number(r.cadence ?? 0),
      hr: Number(r.heartRate ?? 0),
      speed: Number(r.speed ?? 0),
      dist: Number(r.distance ?? 0),
      alt: Number(r.altitude ?? 0),
      lat: Number(r.positionLat ?? 0) / (2 ** 31 / 180),
      lon: Number(r.positionLong ?? 0) / (2 ** 31 / 180),
      grade: Number(r.grade ?? 0),
      gear: Number(dev[0] ?? 0),
      mode: 'sim',
    };
    if (dev[1] !== undefined) s.gSend = Number(dev[1]);
    if (dev[2] !== undefined) s.pReq = Number(dev[2]);
    return s;
  });
}

let timer: ReturnType<typeof setInterval> | null = null;
/** Sync on start (when logged in) and every 5 minutes. */
export function startBackgroundSync(): void {
  if (timer) return;
  void syncNow();
  timer = setInterval(() => void syncNow(), 5 * 60_000);
}
